import { UseQueryResult } from 'react-query';
import { createSearchParamsWithArray } from 'Iaso/libs/utils';
import { openSnackBar } from '../../components/snackBars/EventDispatcher';
import { errorSnackBar } from '../../constants/snackBars';
import {
    getRequest,
    patchRequest,
    postRequest,
    putRequest,
} from '../../libs/Api';
import { useSnackQuery } from '../../libs/apiHooks';
import { Form, FormWritePayload } from './types/forms';

export const useGetForm = (
    formId: number | string | undefined,
    enabled = Boolean(formId) && formId !== '0',
    fields?: string,
    appId?: string,
    extraQueryParams?: Record<string, string>,
): UseQueryResult<Form, Error> => {
    const queryKey: any[] = ['forms', formId];
    if (fields) {
        queryKey.push(fields);
    }
    if (extraQueryParams) {
        queryKey.push(extraQueryParams);
    }

    const params = {
        ...(appId ? { app_id: appId } : {}),
        ...(fields ? { fields: fields } : {}),
        ...extraQueryParams,
    };
    const url = `/api/forms/${formId}/?${createSearchParamsWithArray(params).toString()}`;
    return useSnackQuery({
        queryKey,
        queryFn: () => getRequest(url),
        options: {
            retry: false,
            enabled,
            staleTime: 60000,
            cacheTime: 1000 * 60 * 5,
        },
    });
};

export const createForm = (formData: FormWritePayload): Promise<Form> =>
    postRequest('/api/forms/', formData).catch(error => {
        openSnackBar(errorSnackBar('createFormError', null, error));
        throw error;
    });

export const updateForm = (
    formId: number,
    formData: FormWritePayload,
): Promise<Form> =>
    putRequest(`/api/forms/${formId}/`, formData).catch(error => {
        openSnackBar(errorSnackBar('updateFormError', null, error));
        throw error;
    });

export type FormVersionDiff = {
    previous_version_id: string | null;
    removed_questions: { name: string; label: string; type: string }[];
    added_questions: { name: string; label: string; type: string }[];
    modified_questions: {
        name: string;
        label: string;
        old_type: string;
        new_type: string;
    }[];
};
type FormVersionData = {
    data: Record<string, any>;
    xls_file: File;
};

export const previewFormVersion = (
    formVersionData: FormVersionData,
): Promise<FormVersionDiff> => {
    const { data } = formVersionData;
    const fileData = { xls_file: formVersionData.xls_file };
    return postRequest('/api/formversions/preview/', data, fileData);
};

export const createFormVersion = (formVersionData: FormVersionData) => {
    const { data } = formVersionData;
    const fileData = { xls_file: formVersionData.xls_file };

    return postRequest('/api/formversions/', data, fileData).catch(error => {
        console.error(
            'Error details:',
            error.details,
            'Error message:',
            error.message,
            'Error body:',
            error.body,
        );
        openSnackBar(errorSnackBar('createFormVersionError', null, error));
        throw error;
    });
};

export const updateFormVersion = (data: Record<string, any>) => {
    return patchRequest(`/api/formversions/${data.id}/`, data).catch(error => {
        openSnackBar(errorSnackBar('updateFormVersionError', null, error));
        throw error;
    });
};
